"use client";

import React from "react";
import Link from "next/link";
import LogoBrand from "@/components/ui/LogoBrand";

const serviceLinks = [
  { label: "IT Infrastructure",          href: "/it-infrastructure" },
  { label: "Managed IT Services",       href: "/managed-it-services" },
  { label: "Network Solutions",         href: "/network-solutions" },
  { label: "Cybersecurity",             href: "/cyber-security" },
  { label: "Cloud & Microsoft 365",     href: "/cloud-solutions/microsoft-365" },
  { label: "Business Communications",   href: "/business-communications" },
  { label: "AI & Automation",           href: "/ai-automation" },
];

const companyLinks = [
  { label: "About",        href: "/about" },
  { label: "Case Studies", href: "/case-studies" },
  { label: "Insights",     href: "/insights" },
  { label: "Industries",   href: "/industries/retail" },
  { label: "Contact",      href: "/contact" },
  { label: "Book a Call",  href: "/book" },
  { label: "Privacy",      href: "/privacy" },
];

export default function Footer() {
  return (
    <footer
      className="relative"
      style={{
        background: "#070B14",
      }}
    >
      {/* Luminous top border gradient */}
      <div
        className="absolute top-0 left-0 right-0 h-px"
        style={{
          background: "linear-gradient(90deg, transparent 0%, rgba(37,99,235,0.6) 20%, rgba(59,130,246,0.8) 50%, rgba(34,211,238,0.5) 70%, transparent 100%)",
        }}
      />
      {/* Subtle top glow */}
      <div
        className="absolute top-0 left-0 right-0 h-24 pointer-events-none"
        style={{
          background: "linear-gradient(to bottom, rgba(37,99,235,0.06) 0%, transparent 100%)",
        }}
      />

      <div className="relative max-w-container mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 sm:gap-12 mb-10 sm:mb-14">

          {/* Brand column */}
          <div className="lg:col-span-1">
            <div className="mb-6">
              <LogoBrand size="sm" />
            </div>
            <p className="text-[14px] font-body leading-relaxed mb-4" style={{ color: "rgba(148,163,184,0.7)" }}>
              Pro IT Solutions is an Auckland-based Managed IT Services provider that designs, deploys, secures, and supports technology environments for growing New Zealand businesses.
            </p>
            <p className="text-[14px] font-body leading-relaxed mb-6" style={{ color: "rgba(148,163,184,0.7)" }}>
              We specialise in multi-site businesses — retail chains, franchise networks, hospitality groups, and professional services firms across the country.
            </p>
            {/* Social links */}
            <div className="flex items-center gap-3">
              {[
                { label: "LinkedIn", href: "#", icon: (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"/>
                    <rect x="2" y="9" width="4" height="12"/><circle cx="4" cy="4" r="2"/>
                  </svg>
                )},
                { label: "Facebook", href: "#", icon: (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/>
                  </svg>
                )},
              ].map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  aria-label={social.label}
                  className="w-9 h-9 rounded-xl flex items-center justify-center transition-all duration-300"
                  style={{
                    background: "rgba(255,255,255,0.05)",
                    border: "1px solid rgba(255,255,255,0.08)",
                    color: "#94A3B8",
                  }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLAnchorElement).style.background = "rgba(37,99,235,0.15)";
                    (e.currentTarget as HTMLAnchorElement).style.borderColor = "rgba(59,130,246,0.4)";
                    (e.currentTarget as HTMLAnchorElement).style.color = "#60A5FA";
                    (e.currentTarget as HTMLAnchorElement).style.transform = "translateY(-2px)";
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLAnchorElement).style.background = "rgba(255,255,255,0.05)";
                    (e.currentTarget as HTMLAnchorElement).style.borderColor = "rgba(255,255,255,0.08)";
                    (e.currentTarget as HTMLAnchorElement).style.color = "#94A3B8";
                    (e.currentTarget as HTMLAnchorElement).style.transform = "translateY(0)";
                  }}
                >
                  {social.icon}
                </a>
              ))}
            </div>
          </div>

          {/* Services column */}
          <div>
            <h4 className="font-heading font-600 text-[11px] uppercase tracking-widest mb-6 gradient-text">
              Services
            </h4>
            <ul className="flex flex-col gap-3.5">
              {serviceLinks.map((link) => (
                <li key={link.href}>
                  <Link
                    href={link.href}
                    className="text-[14px] font-body font-500 transition-all duration-200 flex items-center gap-2 group"
                    style={{ color: "rgba(148,163,184,0.7)" }}
                    onMouseEnter={(e) => {
                      (e.currentTarget as HTMLAnchorElement).style.color = "#F8FAFC";
                      (e.currentTarget as HTMLAnchorElement).style.paddingLeft = "4px";
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as HTMLAnchorElement).style.color = "rgba(148,163,184,0.7)";
                      (e.currentTarget as HTMLAnchorElement).style.paddingLeft = "0";
                    }}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company column */}
          <div>
            <h4 className="font-heading font-600 text-[11px] uppercase tracking-widest mb-6 gradient-text">
              Company
            </h4>
            <ul className="flex flex-col gap-3.5">
              {companyLinks.map((link) => (
                <li key={link.href}>
                  <Link
                    href={link.href}
                    className="text-[14px] font-body font-500 transition-all duration-200"
                    style={{ color: "rgba(148,163,184,0.7)" }}
                    onMouseEnter={(e) => {
                      (e.currentTarget as HTMLAnchorElement).style.color = "#F8FAFC";
                      (e.currentTarget as HTMLAnchorElement).style.paddingLeft = "4px";
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as HTMLAnchorElement).style.color = "rgba(148,163,184,0.7)";
                      (e.currentTarget as HTMLAnchorElement).style.paddingLeft = "0";
                    }}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact column */}
          <div>
            <h4 className="font-heading font-600 text-[11px] uppercase tracking-widest mb-6 gradient-text">
              Contact
            </h4>
            <div className="flex flex-col gap-5">
              <div>
                <div className="text-[11px] font-body uppercase tracking-wider mb-1.5" style={{ color: "rgba(148,163,184,0.5)" }}>Phone</div>
                <a
                  href="tel:0800776481"
                  className="font-heading font-600 text-[16px] transition-colors duration-200"
                  style={{ color: "#60A5FA" }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = "#93C5FD"; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = "#60A5FA"; }}
                >
                  0800 77 6481
                </a>
              </div>
              <div>
                <div className="text-[11px] font-body uppercase tracking-wider mb-1.5" style={{ color: "rgba(148,163,184,0.5)" }}>Email</div>
                <a
                  href="mailto:info@proitsolutions.nz"
                  className="text-[14px] font-body transition-colors duration-200"
                  style={{ color: "rgba(148,163,184,0.7)" }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = "#F8FAFC"; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = "rgba(148,163,184,0.7)"; }}
                >
                  info@proitsolutions.nz
                </a>
              </div>
              <div>
                <div className="text-[11px] font-body uppercase tracking-wider mb-1.5" style={{ color: "rgba(148,163,184,0.5)" }}>Address</div>
                <p className="text-[14px] font-body leading-relaxed" style={{ color: "rgba(148,163,184,0.6)" }}>
                  K1/75 Corinthian Drive<br />
                  Albany, Auckland 0632
                </p>
              </div>
              <div>
                <div className="text-[11px] font-body uppercase tracking-wider mb-1.5" style={{ color: "rgba(148,163,184,0.5)" }}>Hours</div>
                <p className="text-[14px] font-body" style={{ color: "rgba(148,163,184,0.6)" }}>
                  Mon–Fri, 8am–6pm NZST
                </p>
              </div>
              <div>
                <div className="text-[11px] font-body uppercase tracking-wider mb-1.5" style={{ color: "rgba(148,163,184,0.5)" }}>Response Time</div>
                <p className="text-[14px] font-body" style={{ color: "rgba(148,163,184,0.6)" }}>
                  &lt;2 hours during business hours
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Trust signals strip */}
        <div
          className="py-4 mb-4 text-center"
          style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}
        >
          <p className="text-xs font-body" style={{ color: "rgba(255,255,255,0.3)" }}>
            ✓ No Lock-in Contracts &nbsp;&nbsp; ✓ 100% Satisfaction Guarantee &nbsp;&nbsp; ✓ NZ-Owned &amp; Operated &nbsp;&nbsp; ✓ 24/7 Monitoring
          </p>
        </div>

        {/* Bottom bar */}
        <div
          className="pt-4 flex flex-col sm:flex-row items-center justify-between gap-4"
          style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}
        >
          <p className="text-[13px] font-body" style={{ color: "rgba(148,163,184,0.4)" }}>
            © 2026 Pro IT Solutions Ltd. All rights reserved.
          </p>
          <div className="flex items-center gap-6">
            <Link
              href="/privacy"
              className="text-[13px] font-body transition-colors duration-200"
              style={{ color: "rgba(148,163,184,0.4)" }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = "rgba(148,163,184,0.8)"; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = "rgba(148,163,184,0.4)"; }}
            >Privacy Policy</Link>
            <Link
              href="/terms"
              className="text-[13px] font-body transition-colors duration-200"
              style={{ color: "rgba(148,163,184,0.4)" }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = "rgba(148,163,184,0.8)"; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = "rgba(148,163,184,0.4)"; }}
            >Terms of Service</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}